import { EDevFlag } from '@/constants';
import Cookies from 'js-cookie';
import { initDevLogger } from './logger.dev';
import AppConfig, { IS_DEVELOPMENT_ENV } from '@/AppConfig';

export interface IDevTools {
  hasDevTools: boolean;
  setWebView(enabled?: boolean): void;
  setDevFlag(flag: EDevFlag): void;
  isDevFlagSet(flag: EDevFlag): boolean;
}

export class DevTools implements IDevTools {
  hasDevTools = AppConfig.DEV_TOOLS_ENABLED;

  private refreshPage() {
    window.location.reload();
  }

  private getFlagsFromParams() {
    const params = new URLSearchParams(window.location.search);
    const addFlags = params.get('SET_DEV_FLAGS')?.split(',');
    const removeFlags = params.get('REMOVE_DEV_FLAGS')?.split(',');
    return {
      add: (addFlags ?? []) as Array<EDevFlag>,
      remove: (removeFlags ?? []) as Array<EDevFlag>,
      clear: params.has('CLEAR_DEV_FLAGS'),
    };
  }

  private removeFlagsFromParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('SET_DEV_FLAGS');
    params.delete('REMOVE_DEV_FLAGS');
    params.delete('CLEAR_DEV_FLAGS');
    if (params.size === 0) {
      window.location.search = '';
    } else {
      window.location.search = params?.toString();
    }
  }

  constructor() {
    if (!this.hasDevTools) return;
    const { add, clear, remove } = this.getFlagsFromParams();

    const shouldModifyFlags = add?.length > 0 || remove?.length > 0 || clear;

    if (shouldModifyFlags) {
      if (clear) {
        this.clearDevFlags(false);
      } else {
        if (add?.length > 0) {
          add.forEach((flag) => {
            if (EDevFlag?.[flag]) {
              this.setDevFlag(flag);
            }
          });
        }
        if (remove?.length > 0) {
          remove.forEach((flag) => {
            if (EDevFlag?.[flag]) {
              this.removeDevFlag(flag);
            }
          });
        }
      }
      this.removeFlagsFromParams();
    }
  }

  setWebView(enabled?: boolean) {
    if (!this.hasDevTools) return;
    if (enabled) {
      Cookies.set('isWebView', 'true');
    } else {
      Cookies.remove('isWebView');
    }
    this.refreshPage();
  }

  setDevFlag(flag: EDevFlag) {
    if (!this.hasDevTools) return;
    Cookies.set(flag, AppConfig.DEV_FLAG_VALUE);
    this.refreshPage();
  }

  removeDevFlag(flag: EDevFlag) {
    if (!this.hasDevTools) return;
    Cookies.remove(flag);
    this.refreshPage();
  }

  clearDevFlags(reload: boolean = true) {
    const allFlags = Object.values(EDevFlag);
    allFlags.forEach((flag) => {
      this.removeDevFlag(flag);
    });

    if (reload) {
      this.refreshPage();
    }
  }

  isDevFlagSet(flag: EDevFlag) {
    if (!this.hasDevTools) return false;
    return Cookies.get(flag) === AppConfig.DEV_FLAG_VALUE;
  }
}

// Initialize the dev logger
export const initDevTools = () => {
  if (typeof window !== 'undefined') {
    const devTools = new DevTools();
    window.devTools = devTools;
    initDevLogger(
      Boolean(devTools.isDevFlagSet(EDevFlag.SHOW_LOGS) || IS_DEVELOPMENT_ENV)
    );
  }
};
